/**
 * Helpers for the Home Builder "Custom HTML" homepage mode.
 * Navbar stays outside (App.vue); HTML is embedded in an iframe srcdoc.
 */

export function isHomepageHtmlMode(mode: unknown): boolean {
  return String(mode || '').toLowerCase() === 'html'
}

function getPageOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin
  }
  return ''
}

/**
 * srcdoc iframes resolve relative URLs poorly without a <base>.
 * Also rewrite absolute http(s) same-host URLs to the current origin so
 * forced-HTTPS hosts do not hit mixed-content blocks on http:// image links.
 */
function normalizeHomepageHtml(html: string, origin: string): string {
  let out = html

  if (origin) {
    const host = origin.replace(/^https?:\/\//i, '')
    const escapedHost = host.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    out = out.replace(
      new RegExp(`https?:\\/\\/${escapedHost}(?=[\\/'"\\s>])`, 'gi'),
      origin,
    )
  }

  const baseTag = origin ? `<base href="${origin}/">` : ''
  // Neutralize 100vh inside srcdoc — vh tracks iframe height and creates infinite growth
  const embedCss = `<style id="strada-homepage-embed">html,body{min-height:0!important;height:auto!important;}[style*="100vh"],.hero,.Hero{min-height:0!important;} .hero{min-height:720px!important;} @media(max-width:900px){.hero{min-height:0!important;}}</style>`
  const headInject = `${baseTag}${embedCss}`
  if (headInject) {
    if (/<head[^>]*>/i.test(out)) {
      out = out.replace(/<head[^>]*>/i, (m) => `${m}\n${headInject}`)
    } else if (/<html[^>]*>/i.test(out)) {
      out = out.replace(/<html[^>]*>/i, (m) => `${m}\n<head>${headInject}</head>`)
    } else {
      out = `<head>${headInject}</head>${out}`
    }
  }

  // Tell parent page the true content height (cap avoids vh feedback spam)
  const heightScript = `<script>(function(){var MAX=20000,t;function p(){try{var h=Math.max(document.body?document.body.scrollHeight:0,document.documentElement?document.documentElement.scrollHeight:0);if(h>MAX)h=MAX;parent.postMessage({type:'strada-homepage-html-height',height:h},'*')}catch(e){}}function s(){clearTimeout(t);t=setTimeout(p,50)}window.addEventListener('load',p);window.addEventListener('resize',s);document.addEventListener('DOMContentLoaded',p);setTimeout(p,0);setTimeout(p,300);setTimeout(p,900);try{document.querySelectorAll('img').forEach(function(i){i.addEventListener('load',s);i.addEventListener('error',s)})}catch(e){}})();</script>`

  if (/<\/body>/i.test(out)) {
    out = out.replace(/<\/body>/i, `${heightScript}</body>`)
  } else {
    out += heightScript
  }

  return out
}

/**
 * Build an iframe srcdoc string from admin-pasted HTML.
 * - Full documents keep styles/scripts (with base + same-origin URL rewrite).
 * - Fragments are wrapped in a minimal document shell.
 */
export function prepareHomepageHtml(raw: string): string {
  const trimmed = (raw || '').trim()
  const origin = getPageOrigin()

  if (!trimmed) {
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">${origin ? `<base href="${origin}/">` : ''}</head><body></body></html>`
  }

  const hasDocument =
    /<html[\s>]/i.test(trimmed) || /<!doctype/i.test(trimmed)

  if (hasDocument) {
    return normalizeHomepageHtml(trimmed, origin)
  }

  return normalizeHomepageHtml(
    `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>html,body{margin:0;padding:0;}</style>
</head>
<body>
${trimmed}
</body>
</html>`,
    origin,
  )
}
